import React from 'react';
import { motion } from 'framer-motion';
import PageTransition from '../components/PageTransition';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Avatar,
} from '@mui/material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
} from '@mui/lab';
import EngineeringIcon from '@mui/icons-material/Engineering';
import StarIcon from '@mui/icons-material/Star';
import GroupIcon from '@mui/icons-material/Group';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import HandshakeIcon from '@mui/icons-material/Handshake';

const About = () => {
  const teamMembers = [
    {
      name: 'John Smith',
      role: 'Lead Technician',
      description: '15+ years of experience in TV installation and setup',
      expertise: ['TV Mounting', 'Home Theater', 'Smart Home Integration'],
    },
    {
      name: 'Sarah Johnson',
      role: 'Smart TV Specialist',
      description: 'Expert in smart home integration and streaming services',
      expertise: ['Smart TV Setup', 'Streaming Services', 'Network Configuration'],
    },
    {
      name: 'Mike Wilson',
      role: 'Customer Support Manager',
      description: 'Dedicated to providing exceptional customer service',
      expertise: ['Customer Service', 'Technical Support', 'Team Management'],
    },
  ];

  const milestones = [
    {
      year: '2008',
      title: 'Company Founded',
      description: 'Started with a vision to provide professional TV installation services',
    },
    {
      year: '2012',
      title: 'Expansion',
      description: 'Expanded services to include smart home integration',
    },
    {
      year: '2015',
      title: 'Industry Recognition',
      description: 'Received "Best TV Installation Service" award',
    },
    {
      year: '2020',
      title: 'National Coverage',
      description: 'Expanded operations to serve customers nationwide',
    },
  ];

  const achievements = [
    {
      icon: <EmojiEventsIcon sx={{ fontSize: 40 }} />,
      title: 'Best Service Award',
      description: '3 consecutive years of industry recognition',
    },
    {
      icon: <WorkspacePremiumIcon sx={{ fontSize: 40 }} />,
      title: 'Certified Excellence',
      description: 'All technicians are certified and regularly trained',
    },
    {
      icon: <HandshakeIcon sx={{ fontSize: 40 }} />,
      title: 'Customer Trust',
      description: '98% customer satisfaction rate',
    },
  ];

  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const staggerChildren = {
    animate: {
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  return (
    <PageTransition>
      <Box>
        {/* About Header */}
        <Box sx={{ bgcolor: 'primary.main', color: 'white', py: 8 }}>
          <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
            <motion.div {...fadeInUp}>
              <Typography 
                variant="h2" 
                component="h1" 
                gutterBottom 
                sx={{ 
                  textAlign: 'center',
                  fontSize: { xs: '2.5rem', sm: '3rem', md: '3.5rem' },
                  fontWeight: 700,
                  mb: 3,
                  color: 'white'
                }}
              >
                About Us
              </Typography>
            </motion.div>
            <motion.div {...fadeInUp}>
              <Typography 
                variant="h5" 
                sx={{ 
                  mb: 4,
                  textAlign: 'center',
                  maxWidth: '900px',
                  margin: '0 auto',
                  opacity: 0.9,
                  fontSize: { xs: '1.2rem', sm: '1.4rem', md: '1.6rem' },
                  lineHeight: 1.4,
                }}
              >
                Your Trusted TV Setup and Activation Specialists
              </Typography>
            </motion.div>
          </Container>
        </Box>

        {/* Mission Statement */}
        <Container sx={{ py: 8 }}>
          <motion.div {...fadeInUp}>
            <Box sx={{ textAlign: 'center', mb: 8 }}>
              <Typography variant="h3" component="h2" gutterBottom>
                Our Mission
              </Typography>
              <Typography variant="h5" color="text.secondary" sx={{ maxWidth: 800, mx: 'auto' }}>
                To provide exceptional TV installation and setup services that enhance our customers' entertainment experience through professional expertise and dedication to quality.
              </Typography>
            </Box>
          </motion.div>

          {/* Company Info */}
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <motion.div {...fadeInUp}>
                <Typography variant="h4" component="h2" gutterBottom>
                  Our Story
                </Typography>
                <Typography variant="body1" paragraph>
                  With over a decade of experience in TV installation and setup services,
                  we've built our reputation on providing exceptional service and
                  technical expertise to our customers.
                </Typography>
                <Typography variant="body1" paragraph>
                  What started as a small local business has grown into a trusted name in TV installation and setup services. Our commitment to quality and customer satisfaction has helped us build lasting relationships with our clients.
                </Typography>
                <Typography variant="body1">
                  We understand that modern TV setup can be complex, which is why we're
                  here to make the process simple and stress-free for you.
                </Typography>
              </motion.div>
            </Grid>
            <Grid item xs={12} md={6}>
              <motion.div variants={staggerChildren} initial="initial" animate="animate">
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {[
                    { icon: <EngineeringIcon color="primary" sx={{ fontSize: 40 }} />, title: 'Expert Technicians', desc: 'Certified professionals with years of experience' },
                    { icon: <StarIcon color="primary" sx={{ fontSize: 40 }} />, title: 'Quality Service', desc: '100% satisfaction guaranteed on all services' },
                    { icon: <GroupIcon color="primary" sx={{ fontSize: 40 }} />, title: 'Customer Focus', desc: 'Dedicated to exceeding customer expectations' }
                  ].map((item, index) => (
                    <motion.div key={index} variants={fadeInUp}>
                      <Card>
                        <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          {item.icon}
                          <Box>
                            <Typography variant="h6">{item.title}</Typography>
                            <Typography variant="body2">{item.desc}</Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </motion.div>
                  ))}
                </Box>
              </motion.div>
            </Grid>
          </Grid>
        </Container>

        {/* Company Timeline */}
        <Box sx={{ bgcolor: 'grey.100', py: 8 }}>
          <Container>
            <Typography variant="h3" component="h2" textAlign="center" gutterBottom>
              Our Journey
            </Typography>
            <Timeline position="alternate" sx={{ mt: 4 }}>
              {milestones.map((milestone, index) => (
                <TimelineItem key={index}>
                  <TimelineSeparator>
                    <TimelineDot color="primary" />
                    <TimelineConnector />
                  </TimelineSeparator>
                  <TimelineContent>
                    <Card sx={{ p: 2 }}>
                      <Typography variant="h6" color="primary">
                        {milestone.year}
                      </Typography>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {milestone.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {milestone.description}
                      </Typography>
                    </Card>
                  </TimelineContent>
                </TimelineItem>
              ))}
            </Timeline>
          </Container>
        </Box>

        {/* Achievements */}
        <Container sx={{ py: 8 }}>
          <Typography variant="h3" component="h2" textAlign="center" gutterBottom>
            Our Achievements
          </Typography>
          <Grid container spacing={4} sx={{ mt: 2 }}>
            {achievements.map((achievement, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card sx={{ textAlign: 'center', height: '100%', p: 3 }}>
                  <Box sx={{ color: 'primary.main', mb: 2 }}>
                    {achievement.icon}
                  </Box>
                  <Typography variant="h5" gutterBottom>
                    {achievement.title}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {achievement.description}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>

        {/* Team Section */}
        <Box sx={{ bgcolor: 'grey.100', py: 8 }}>
          <Container>
            <Typography variant="h3" component="h2" textAlign="center" gutterBottom>
              Meet Our Team
            </Typography>
            <Typography variant="subtitle1" textAlign="center" color="text.secondary" sx={{ mb: 6 }}>
              Our experienced professionals are here to help you
            </Typography>
            <Grid container spacing={4}>
              {teamMembers.map((member, index) => (
                <Grid item xs={12} md={4} key={index}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Avatar
                        sx={{
                          width: 100,
                          height: 100,
                          margin: '0 auto 16px',
                          bgcolor: 'primary.main',
                        }}
                      >
                        {member.name.charAt(0)}
                      </Avatar>
                      <Typography variant="h5" component="h3" gutterBottom>
                        {member.name}
                      </Typography>
                      <Typography
                        variant="subtitle1"
                        color="primary"
                        gutterBottom
                      >
                        {member.role}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" paragraph>
                        {member.description}
                      </Typography>
                      <Box sx={{ mt: 2 }}>
                        {member.expertise.map((skill, idx) => (
                          <Typography
                            key={idx}
                            variant="body2"
                            component="span"
                            sx={{
                              display: 'inline-block',
                              bgcolor: 'primary.main',
                              color: 'white',
                              px: 1,
                              py: 0.5,
                              borderRadius: 1,
                              mr: 1,
                              mb: 1,
                            }}
                          >
                            {skill}
                          </Typography>
                        ))}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>
      </Box>
    </PageTransition>
  );
};

export default About; 